import { useEffect, useState, useRef, useMemo } from 'react';
import { connect } from 'react-redux';
import Container from 'react-bootstrap/Container';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import ReportsSidebar from './sidebarV3';
import { applyFilters } from '../../components/Reporting/applyFilters';
import ReportPage from './reportPage';
import isEqual from 'lodash/isEqual';
import { getDefaultDateRangeForReport } from '../../common/utils/dateRangeOptions';
import { normalizeReportFilters } from './Filters/reportFilterRegistry';
import { buildDynamicOptionsFromFilters } from './Filters/reportFilterUtils';


const useDebounce = ( value, delay = 300 ) => {
    const [ debouncedValue, setDebouncedValue ] = useState( value );

    useEffect( () => {
        const handler = setTimeout( () => setDebouncedValue( value ), delay );
        return () => clearTimeout( handler );
    }, [ value, delay ] );

    return debouncedValue;
};


const ReportsDashboard = ( { user, viewing, loans, contacts, opps, schema, reporting } ) => {
    const [ containerBuffer, setContainerBuffer ] = useState( 115 );
    const [ gridHeight, setGridHeight ] = useState( 0 );
    const defaultFilters = {
        dateRange: 'last90days',
        startDate: null,
        endDate: null,
        loanPurpose: [],
        loanType: [],
        loanSource: [],
        referralSource: [],
        state: [],
        county: [],
        city: [],
        zip: [],
        rank: [],
        partnerRank: [],
        closed: null,
        referred: null,
        minPurchasePrice: '',
        maxPurchasePrice: '',
        minLoanAmount: '',
        maxLoanAmount: '',
        conversionMilestones: [],
    };
    const [ selectedReport, setSelectedReport ] = useState( null );
    const [ filters, setFilters ] = useState( defaultFilters );
    const [ dynamicOptions, setDynamicOptions ] = useState( {
        loanPurpose: [],
        loanType: [],
        loanSource: [],
        referralSource: [],
        cities: [],
        states: [],
        zips: [],
        counties: [],
        conversionMilestones: [
            { label: 'Opp Date', value: 'oppDate' },
            { label: 'App Started Date', value: 'appStartedDate' },
            { label: 'App Completed Date', value: 'appCompletedDate' },
            { label: 'Pre-Qualified Date', value: 'pqDate' },
            { label: 'Pre-Approval Date', value: 'paDate' },
            { label: 'In Contract Date', value: 'inContractDate' },
            { label: 'Funded Date', value: 'fundedDate' }
        ]
    } );
    
    const prev = useRef( null );
    const containerRef = useRef();
    
    const visibleLoanData = useMemo(() => {
        if (!loans) return [];
    
        return Object.values(loans)
            .filter((loan) => viewing.includes(loan.aid))
            .map((loan) => {
                const pri = loan.borrowers.find( f => f.label === 'Primary' ) || loan.borrowers[0];
    
                return {
                    ...loan,
                    ...contacts[pri?.cid]
                };
            });
    }, [loans, contacts, viewing]);
    
    const filterDefs = useMemo(() => {
        return normalizeReportFilters( selectedReport || {} );
    }, [selectedReport]);

    const filteredData = useMemo( () => {
        if ( !selectedReport ) return [];
        
        const loanArray = [];
        const oppArray = [];
        const conArray = [];

        Object.keys( loans ).forEach( lid => {
            if ( viewing.includes( loans[lid].aid ) ) {
                loanArray.push( loans[lid] );
            }
        } );
        Object.keys( opps ).forEach( loid => {
            if ( viewing.includes( opps[loid].aid ) ) {
                oppArray.push( opps[loid] );
            }
        } );
        Object.keys( contacts ).forEach( cid => {
            if ( viewing.includes( contacts[cid].aid ) || contacts[cid].share === true ) {
                conArray.push( contacts[cid] );
            }
        } );

        const rawData = [];
        switch ( selectedReport.id ) {
            case 'pipe-lead-tracker':
                for ( const lo of oppArray ) {
                    const pri = lo.relationships.find( f => f.label === 'primary' ) || lo.relationships[0];
                    const c = contacts[pri?.cid];
                    const l = loans[pri?.lid] || {};

                    rawData.push( {
                        ...c,
                        ...lo,
                        ...l,
                        loanSource: lo.source,
                    } );
                }
                break;

            case 'pipe-cancelled':
                for ( const l of loanArray ) {
                    if ( !l.cancelledDate ) {
                        continue;
                    }
                    const lo = oppArray.find( f => f.relationships?.find( fa => fa.lid === l.lid ) );
                    rawData.push( {
                        ...lo,
                        ...l,
                        loanSource: lo?.source,
                    } );
                }
                for ( const lo of oppArray ) {
                    if ( lo.status !== 'Cancelled' ) {
                        continue;
                    }
                    const hasLoan = lo.relationships?.find( f => f.lid );
                    if ( !hasLoan ) {
                        rawData.push( {
                            ...lo
                        } );
                    }
                }
                break;

            case 'pipe-fund-summary':
            case 'amr':
                for ( const l of loanArray ) {
                    if ( !l.fundedDate ) {
                        continue;
                    }
                    const lo = oppArray.find( f => f.relationships?.find( fa => fa.lid === l.lid ) );
                    rawData.push( {
                        ...lo,
                        ...l,
                        loanSource: lo?.source,
                    } );
                }
                break;

            case 'pipe-conversion':
            case 'cohort-conversion':
                for ( const l of loanArray ) {
                    const lo = oppArray.find( f => f.relationships?.find( fa => fa.lid === l.lid ) );
                    const borr = l.borrowers.find( f => f.label === 'Primary' ) || l.borrowers[0];
                    const c = contacts[borr?.cid];

                    rawData.push( {
                        ...lo,
                        ...l,
                        ...c,
                        loanSource: lo?.source,
                        milestones: {
                            opp: lo?.oppDate,
                            firstContact: c?.firstContactDate || c?.intakePurchInitialCallDate,
                            appSent: l.appSendDate,
                            appStarted: l.appStartedDate,
                            appCompleted: l.appCompletedDate || l.appCopiedDate,
                            pq: l.pqDate,
                            creditPull: l.creditPullDate,
                            pa: l.paDate,
                            pqa: l.paDate || l.pqDate,
                            active: l.activeDate || l.contractDate || l.respaTriggeredDate,
                            inProcessing: l.inProcessingDate,
                            inUW: l.inUWDate,
                            condApproval: l.conditionalApprovalDate,
                            resubmittal: l.conditionsSubmittedDate,
                            ctc: l.ctcDate,
                            funded: l.fundedDate
                        }
                    } );
                }
                for ( const lo of oppArray ) {
                    if ( !lo.relationships?.length ) {
                        continue;
                    }
                    
                    const hasLoan = lo.relationships.find( f => f.lid );
                    if ( hasLoan ) {
                        continue;
                    }

                    const pri = lo.relationships.find( f => f.label === 'Primary' ) || lo.relationships[0];
                    const c = contacts[pri?.cid];
                    if ( !c ) {
                        continue;
                    }

                    rawData.push( { 
                        ...lo,
                        ...c,
                        loanSource: lo.source,
                        milestones: {
                            opp: lo.oppDate,
                            firstContact: c.firstContactDate || c.intakePurchInitialCallDate,
                        }
                    } );
                }
                break;

            case 'partner-leaderboard':
                for ( const lo of oppArray ) {
                    let lid;
                    let refId;
                     
                    if ( !lo.relationships?.length ) {
                        continue;
                    }
                    
                    for ( const lor of lo.relationships ) {
                        if ( lor.lid ) {
                            lid = lor.lid;
                        }
                        const c = contacts[lor.cid];
                        if ( c?.referredById ) {
                            refId = c.referredById;
                        }
                    }

                    if ( !refId ) {
                        continue;
                    }

                    const c = contacts[refId];
                    const l = loans[lid];
                    rawData.push( { 
                        ...lo,
                        ...l,
                        loanSource: lo.source,
                        contact: c
                    } );
                }
                break;

            case 'borrower-bday':
            case 'partner-bday':
                for ( const c of conArray ) {
                    if ( !c.birthday ) {
                        continue;
                    }

                    rawData.push( {
                        ...c,
                    } );
                }
                break;

            default:
        }
        
        if ( prev.current && isEqual( prev.current.filters, filters ) && isEqual( prev.current.rawData, rawData ) ) {
            return prev.current.result;
        }

        const result = applyFilters( rawData, filters, selectedReport );

        prev.current = {
            filters,
            rawData,
            result,
        };
    
        return result;
    }, [ selectedReport, filters, loans, contacts, opps, viewing ] );

    useEffect( () => {
        if ( user.additionalAccounts && user.additionalAccounts.length > 1 ) {
            setContainerBuffer( prev => prev + 30 );
        }
    }, [ user.additionalAccounts ] );

    useEffect( () => {
        setGridHeight( containerRef.current.clientHeight );
    }, [ containerBuffer ] );

    // Add Static Filter Values
    useEffect( () => {
        const obj = {
            loanPurpose: user.meta?.global.picklist.purpose || [],
            loanType: user.meta?.loan.picklist.loanType || [],
            loanSource: user.meta?.global.picklist.source || [],
            referralSource: [],
            cities: [],
            states: [],
            zips: [],
            counties: [],
            steps: [],
        }
        
        setDynamicOptions( prev => { return { ...prev, ...obj } } );
    }, [ user.meta ] );

    useEffect(() => {
        if (
            ['pipe-conversion', 'cohort-conversion'].includes(selectedReport?.id) &&
            !filters.cohortStart
        ) {
            setFilters((prev) => ({
                ...prev,
                cohortStart: 'opp',
            }));
        }
    }, [selectedReport?.id, filters.cohortStart]);

    useEffect(() => {
        setDynamicOptions((prev) => {
            const baseOptions = {
                loanPurpose: prev?.loanPurpose || [],
                loanType: prev?.loanType || [],
                loanSource: prev?.loanSource || [],
                referralSource: prev?.referralSource || [],
                conversionMilestones: prev?.conversionMilestones || [],
                steps: prev?.steps || [],
            };
    
            const nextDynamicOptions = buildDynamicOptionsFromFilters(
                visibleLoanData,
                filterDefs,
                baseOptions
            );
    
            const next = {
                ...prev,
                ...nextDynamicOptions,
            };
    
            return isEqual(prev, next) ? prev : next;
        });
    }, [filterDefs, visibleLoanData]);

    return (
        <Container fluid className='p-0' style={ { height: '100%' } }>
            <Row  style={ { height: `calc(100vh - ${containerBuffer}px)` } } ref={containerRef}>
                <Col className='p-0' xs={3} md={2} style={ { height: '100%', overflowY: 'auto' } }>
                    <ReportsSidebar 
                        sidebarConfig={reporting} 
                        selectedReportId={selectedReport?.id} 
                        onSelectReport={ ( report ) => {
                            setSelectedReport( report );
                            const df = { ...defaultFilters };
                            df.dateRange = getDefaultDateRangeForReport( report );
                            setFilters( df );
                        } }
                    />
                </Col>
                <Col className='p-0' xs={9} md={10} style={ { height: '100%', overflowY: 'auto' } }>
                    <Container fluid className="p-0">
                        <Row className="tf-section-container p-0 me-4">
                            <ReportPage
                                selectedReport={selectedReport}
                                filters={filters}
                                setFilters={setFilters}
                                dynamicOptions={dynamicOptions}
                                schema={schema[selectedReport?.id]}
                                data={filteredData}
                            />
                        </Row>
                    </Container>
                </Col>
            </Row>
        </Container>
    )
};


const mapProps = ( state ) => ( {
    orgId: state.user.orgId,
    aid: state.user.aid,
    uid: state.user.email,
    user: state.user,
    grids: state.user.meta.datagrids,
    schema: state.user.meta.gridschema,
    reporting: state.user.meta.reporting,
    viewing: state.user.viewing,
    canExport: state.user.canExport,
    loans: state.loans.byId,
    contacts: state.contacts.byId,
    opps: state.leadopps.byId,
    tasks: state.tasks.byId,
    status: state.user.meta.loan.picklist.status
} );
  
const mapDispatch = ( dispatch ) => ( {
    setDatagridWidth: dispatch.user.setDatagridWidth,
    setDatagridOrder: dispatch.user.setDatagridOrder
} );
  
export default connect( mapProps, mapDispatch )( ReportsDashboard );